import { useEffect, useLayoutEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { ProductCard } from './ProductCard';
import { InStorePicksIcon } from '@/shared/icons/icons';
import { ListingDesignItem } from '../../domain/models/ProductListing';
import { PlpProductViewTracker } from './PlpProductViewTracker';
import { BannerWidgetSlot } from '@/shared/features/banner-widget/presentation/components/BannerWidgetSlot';
import { ExploreOtherDesignsWidget, type ExploreOtherDesignsLink } from '@/shared/widgets/ExploreOtherDesignsWidget';
import { StoreLocationWidget } from '@/shared/features/physical-store/presentation/components/StoreLocationWidget';
import { findRowIndexForDesignId, getPlpProductScrollDepth, type PlpCell } from './plpCells.util';
import { cn } from '@/shared/utils/cn';
import {
  PLP_ROW_HEIGHT_BANNER,
  PLP_ROW_HEIGHT_PRODUCT_ESTIMATE,
  PLP_SENTINEL_HEIGHT,
} from '../../constants';

export interface PlpGridProps {
  cells: PlpCell[];
  columnCount: 2 | 3;
  onBeforeNavigate: (designId?: number) => void;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  onLoadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  exploreLinks?: ExploreOtherDesignsLink[];
  /** When true, PDP opens in a new tab for product cards. */
  openPdpInNewTab?: boolean;
  pincode?: string;
  /** Analytics callbacks threaded to ProductCard */
  onProductClick?: (product: import('../../domain/models/ProductListing').ListingDesignItem, scrollDepth?: number, horizontalPosition?: number) => void;
  onWishlistClick?: (
    product: import('../../domain/models/ProductListing').ListingDesignItem,
    productPosition: number,
  ) => void;
  onWishlistToggle?: (
    product: import('../../domain/models/ProductListing').ListingDesignItem,
    action: 'add' | 'remove',
  ) => void;
  onVideoClick?: (
    product: import('../../domain/models/ProductListing').ListingDesignItem,
    productPosition: number,
  ) => void;
  onVideoCallCartAdd?: (product: import('../../domain/models/ProductListing').ListingDesignItem) => void;
  onViewDetailsClick?: (product: import('../../domain/models/ProductListing').ListingDesignItem) => void;
  /** When true, hides the Book Video Call button on all product cards. */
  hideVideoCallButton?: boolean;
  /** Analytics: fires eventLogger `listing_page_product_viewed` after 2s full visibility */
  onProductViewed?: (params: {
    product: import('../../domain/models/ProductListing').ListingDesignItem;
    scrollDepth: number;
    horizontalPosition: number;
  }) => void;
  viewedProductIds?: Set<number>;
  /** Mobile: saved window scroll Y (pixel restore / fallback when design row is missing). */
  mobileScrollRestoreOffset?: number | null;
  /** Mobile: scroll to the row containing this product (preferred over pixel offset). */
  mobileRestoreDesignId?: number | null;
  /** Called once after layout scroll is applied (clears session restore in usePlpListing). */
  onMobileScrollRestoreComplete?: () => void;
  /** When true, skip intersection-triggered load-more (e.g. footer visible). From `usePlpListing`. */
  shouldPauseInfiniteScroll?: () => boolean;
  /** Window virtualizer overscan rows (default 4). */
  overscan?: number;
}

/**
 * Window-virtualized grid: only visible rows are in the DOM while the page scrolls normally.
 * Uses useWindowVirtualizer so the window is the scroll element.
 */
export function PlpGrid({
  cells,
  columnCount,
  onBeforeNavigate,
  sentinelRef,
  onLoadMore,
  hasMore,
  isLoadingMore,
  openPdpInNewTab = false,
  exploreLinks = [],
  pincode,
  onProductClick,
  onWishlistClick,
  onWishlistToggle,
  onVideoClick,
  onVideoCallCartAdd,
  onViewDetailsClick,
  onProductViewed,
  viewedProductIds,
  mobileScrollRestoreOffset = null,
  mobileRestoreDesignId = null,
  onMobileScrollRestoreComplete,
  shouldPauseInfiniteScroll,
  overscan = 4,
  hideVideoCallButton = false,
}: PlpGridProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  const mobileRestoreDoneRef = useRef(false);

  const shouldPauseInfiniteScrollRef = useRef(shouldPauseInfiniteScroll);
  shouldPauseInfiniteScrollRef.current = shouldPauseInfiniteScroll;

  const productViewTrackingEnabled = Boolean(onProductViewed && viewedProductIds);

  // Map from cell index → sequential product index (0-based), used to mark every 5th product for auto-play.
  const productIndexMap = useMemo(() => {
    const map = new Map<number, number>();
    let count = 0;
    cells.forEach((cell, i) => {
      if (cell.type === 'product') map.set(i, count++);
    });
    return map;
  }, [cells]);

  const renderProductCardBadge = (product: ListingDesignItem) => {
    if (product.sourceType === 'POS_CART' || product.sourceType === 'POS_VIEWED') {
      return (
        <div className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 z-10 flex items-center gap-1 rounded px-[6px] py-[4px] text-[12px] font-bold text-[#103064] shadow-sm transition-opacity duration-200 md:group-hover:opacity-0 md:group-hover:pointer-events-none"
          style={{ background: 'linear-gradient(90deg, #E5F4EF 0%, #EFF4DE 25.48%, #EFEAEE 49.52%, #DDF5F9 91.83%)' }}
        >
          <InStorePicksIcon className="h-3 w-3 shrink-0" />
          <span>Your In-Store Picks</span>
        </div>
      );
    }
    return null;
  };

  const renderProductCard = (
    cell: Extract<PlpCell, { type: 'product' }>,
    cellIdx: number,
    rowIndex: number,
    columnIndex: number,
  ) => {
    const productPosition = productIndexMap.get(cellIdx)! + 1;
    const card = (
      <ProductCard
        product={cell.product}
        badge={renderProductCardBadge(cell.product)}
        onBeforeNavigate={onBeforeNavigate}
        openInNewTab={openPdpInNewTab}
        onProductClick={onProductClick ? (p) => onProductClick(p, rowIndex + 1, columnIndex + 1) : undefined}
        onWishlistClick={onWishlistClick}
        onWishlistToggle={onWishlistToggle}
        onVideoClick={onVideoClick}
        onVideoCallCartAdd={onVideoCallCartAdd}
        onViewDetailsClick={onViewDetailsClick}
        autoPlayVideo={productPosition % 5 === 0}
        productPosition={productPosition}
        hideVideoCallButton={hideVideoCallButton}
      />
    );

    if (!productViewTrackingEnabled) {
      return <div key={cell.product.designId}>{card}</div>;
    }

    return (
      <PlpProductViewTracker
        key={cell.product.designId}
        product={cell.product}
        scrollDepth={getPlpProductScrollDepth(cells, columnCount, rowIndex)}
        horizontalPosition={columnIndex + 1}
        viewedDesignIds={viewedProductIds!}
        fullyVisible={false}
        onProductViewed={onProductViewed!}
      >
        {card}
      </PlpProductViewTracker>
    );
  };

  const shouldMobileScrollRestore =
    Boolean(onMobileScrollRestoreComplete) &&
    (mobileRestoreDesignId != null || mobileScrollRestoreOffset != null);

  const rowCount = Math.ceil(cells.length / columnCount);

  useLayoutEffect(() => {
    if (listRef.current) setScrollMargin(listRef.current.offsetTop);
  }, [rowCount, cells.length]);
  const getRowHeight = useCallback(
    (rowIndex: number) => {
      const start = rowIndex * columnCount;
      const rowCells = cells.slice(start, start + columnCount);
      const isBannerOnly =
        rowCells.length > 0 && rowCells.every(cell => cell.type === 'banner');
      const isExploreLinksOnly =
        rowCells.length > 0 && rowCells.every(cell => cell.type === 'explore-links');
      const isStoreWidgetOnly =
        rowCells.length > 0 && rowCells.every(cell => cell.type === 'store-location-widget');
      if (isExploreLinksOnly || isStoreWidgetOnly) return PLP_ROW_HEIGHT_BANNER;
      return isBannerOnly ? PLP_ROW_HEIGHT_BANNER : PLP_ROW_HEIGHT_PRODUCT_ESTIMATE;
    },
    [cells, columnCount]
  );

  const virtualizer = useWindowVirtualizer({
    count: rowCount,
    estimateSize: getRowHeight,
    overscan,
    scrollMargin,
  });

  const virtualizerRef = useRef(virtualizer);
  virtualizerRef.current = virtualizer;

  // Phase 1 (same layout pass as mount): window at top so we never "stick" on the footer.
  useLayoutEffect(() => {
    if (!shouldMobileScrollRestore) return;
    if (typeof window === 'undefined') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [shouldMobileScrollRestore]);

  // Phase 2: after one paint at top, scroll to saved product row or saved Y, sync virtualizer.
  useLayoutEffect(() => {
    if (!shouldMobileScrollRestore || mobileRestoreDoneRef.current) {
      return;
    }
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (mobileRestoreDoneRef.current) return;
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        if (!onMobileScrollRestoreComplete) return;

        const applyPixelScroll = (raw: number) => {
          const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
          const y = Math.min(Math.max(0, raw), maxY);
          window.scrollTo({ top: y, behavior: 'auto' });
          virtualizerRef.current.scrollToOffset(y, { behavior: 'auto' });
        };

        if (mobileRestoreDesignId != null) {
          const rowIndex = findRowIndexForDesignId(cells, columnCount, mobileRestoreDesignId);
          if (rowIndex != null) {
            virtualizerRef.current.scrollToIndex(rowIndex, { align: 'start', behavior: 'auto' });
            mobileRestoreDoneRef.current = true;
            onMobileScrollRestoreComplete();
            return;
          }
          if (mobileScrollRestoreOffset != null) {
            applyPixelScroll(mobileScrollRestoreOffset);
          }
          mobileRestoreDoneRef.current = true;
          onMobileScrollRestoreComplete();
          return;
        }

        if (mobileScrollRestoreOffset != null) {
          applyPixelScroll(mobileScrollRestoreOffset);
        }
        mobileRestoreDoneRef.current = true;
        onMobileScrollRestoreComplete();
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [
    shouldMobileScrollRestore,
    mobileRestoreDesignId,
    mobileScrollRestoreOffset,
    cells,
    columnCount,
    onMobileScrollRestoreComplete,
  ]);

  const totalSize = virtualizer.getTotalSize();
  const sentinelHeight = hasMore ? PLP_SENTINEL_HEIGHT : 0;
  const virtualItems = virtualizer.getVirtualItems();
  const isServer = typeof window === 'undefined';

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || isLoadingMore) return;

    // Track scroll direction so the 800px pre-load zone only fires when scrolling
    // down. When scrolling up from the footer the sentinel must actually be in
    // the real viewport before triggering the next page.
    let lastScrollY = window.scrollY;
    const scrollDirectionRef = { current: 'up' as 'up' | 'down' };
    const onScroll = () => {
      const y = window.scrollY;
      scrollDirectionRef.current = y >= lastScrollY ? 'down' : 'up';
      lastScrollY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        const rect = entry.boundingClientRect;
        const rootBounds = entry.rootBounds;
        if (!rootBounds) return;
        // Only load when sentinel is actually in viewport, not when user has scrolled past (e.g. to SEO content)
        if (rect.bottom <= rootBounds.top || rect.top >= rootBounds.bottom) return;
        // Extended zone (below actual viewport): only preload when scrolling down.
        // When scrolling up from footer, wait until sentinel is actually visible.
        const actualViewportBottom = rootBounds.bottom - 800;
        const inActualViewport = rect.top < actualViewportBottom && rect.bottom > rootBounds.top;
        if (!inActualViewport && scrollDirectionRef.current === 'up') return;
        if (shouldPauseInfiniteScrollRef.current?.() ?? false) return;
        onLoadMore();
      },
      { root: null, rootMargin: '0px 0px 800px 0px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, [hasMore, isLoadingMore, onLoadMore, sentinelRef]);

  if (isServer) {
    return (
      <div className="grid gap-3 grid-cols-2 md:grid-cols-3">
        {cells.map((cell, cellIdx) =>
          cell.type === 'product' ? (
            renderProductCard(cell, cellIdx, Math.floor(cellIdx / columnCount), cellIdx % columnCount)
          ) : null
        )}
      </div>
    );
  }

  return (
    <div ref={listRef}>
      <div
        style={{
          height: `${totalSize + sentinelHeight}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualItems.map((virtualRow) => {
          const start = virtualRow.index * columnCount;
          const rowCells = cells.slice(start, start + columnCount);
          const isBannerOnly =
            rowCells.length > 0 && rowCells.every(cell => cell.type === 'banner');
          const isExploreLinksOnly =
            rowCells.length > 0 && rowCells.every(cell => cell.type === 'explore-links');
          const isStoreWidgetOnly =
            rowCells.length > 0 && rowCells.every(cell => cell.type === 'store-location-widget');

          return (
            <div
              key={virtualRow.key}
              ref={virtualizer.measureElement}
              data-index={virtualRow.index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${
                  virtualRow.start - virtualizer.options.scrollMargin
                }px)`,
                paddingBottom: 12,
              }}
            >
              {isStoreWidgetOnly ? (
                <StoreLocationWidget pincode={pincode} />
              ) : isExploreLinksOnly ? (
                <ExploreOtherDesignsWidget links={exploreLinks} />
              ) : isBannerOnly ? (
                <div className="relative left-1/2 right-1/2 w-screen -ml-[50vw] -mr-[50vw]">
                  <BannerWidgetSlot fullWidth slotId={`banner-row-${virtualRow.index}`} />
                </div>
              ) : (
                <div
                  className={cn(
                    'grid gap-3',
                    columnCount === 2 && 'grid-cols-2',
                    columnCount === 3 && 'grid-cols-3'
                  )}
                >
                  {rowCells.map((cell, j) => {
                    const cellIdx = start + j;
                    return cell.type === 'product' ? (
                      renderProductCard(cell, cellIdx, virtualRow.index, j)
                    ) : cell.type === 'explore-links' || cell.type === 'store-location-widget' ? null : (
                      <BannerWidgetSlot key={cell.id} fullWidth={false} slotId={cell.id} />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {hasMore && (
          <div
            ref={sentinelRef}
            style={{
              position: 'absolute',
              top: totalSize,
              left: 0,
              height: sentinelHeight,
              width: '100%',
            }}
            aria-hidden
          />
        )}
      </div>
    </div>
  );
}
